import { faCalendar } from '@fortawesome/pro-light-svg-icons';
import { faUsers } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import './Challenges.css';
import { Button } from '../shared/components/button/Button';
import LoadingSpinner from '../shared/components/UIElements/LoadingSpinner';
import { HeaderNavigation } from '../shared/headerNavigation/HeaderNavigation';
import WhitePopUpCTA from '../shared/modal/whitePopUp/WhitePopUpCTA';
import {
  getChallengeTypeUnit,
  getFormattedValue,
  getChallengeType,
  getChallengeIcon,
  bringFocusToChallengeNavBtn,
} from '../shared/util/challenges';
import ChallengeErrorModal from './components/ChallengeErrorModal';
import CircleChallengeGraph from './components/CircleChallengeGraph';
import FinishedChallenge from './components/FinishedChallenge';
import LeaderBoard from './components/LeaderBoard';
import { useChallengeConsent } from './hooks/useChallengeConsent';
import { useChallengePage } from './hooks/useChallengePage';

function Challenge() {
  const { t } = useTranslation();
  const {
    challenge,
    isLoading,
    isChallengeVisible,
    isChallengeUnavailable,
    errorText,
    setErrorText,
    showCompletedModal,
    handleCloseCompletedModal,
    toggleActivePage,
    sectionid,
  } = useChallengePage();

  const {
    showConsentModal,
    showSubmitTrackerModal,
    handleToggleConsentSwitch,
    handleCloseModal,
    consentModalContents,
    submitTrackerModalContents,
  } = useChallengeConsent({ challenge, setErrorText, toggleActivePage });

  return (
    <div id="main_container" className="foryou glowrm platform challenge">
      {isLoading && <LoadingSpinner asOverlay />}
      <HeaderNavigation />

      {(isChallengeVisible || isChallengeUnavailable) && !errorText && (
        <div className="page-cont visible">
          <div
            className="results-img"
            style={{
              background: `url(${
                challenge?.background_image ??
                'https://res.cloudinary.com/champion-health/image/upload/v1720000334/Challenges/no-challenge.jpg'
              }) center/cover no-repeat`,
            }}
          >
            <div className="gradient-overlay">
              <div className="hero-contents">
                <p className="hero-subtitle">{t('challenge.hero.title')}</p>
                <h1 className="hero-title">
                  {challenge?.title ?? t('challenge.hero.title.unavailable')}
                </h1>
                <h2
                  className={`challenge-description ${
                    isChallengeUnavailable || !challenge?.isActive
                      ? 'challenge-description-inactive'
                      : ''
                  }`}
                >
                  {challenge?.description ?? t('challenge.hero.description.unavailable')}
                </h2>
                {challenge?.isActive && (
                  <Button
                    className="feature-btn challenges"
                    onClick={() => toggleActivePage(2)}
                    aria-label={`${challenge?.title ?? t('challenge.hero.title.unavailable')} ${t('challenge.hero.button.trackResults')}`}
                  >
                    {t('challenge.hero.button.trackResults')}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="results-container challenges">
            <CircleChallengeGraph
              title={t('challenge.hero.title')}
              subtitle={t('challenge.stats.participants.subtitle')}
              value={challenge?.participantsNumber ?? 0}
              colour="#F69358"
              width="quarter"
              icon={faUsers}
              unit={t('challenge.stats.participants.unit')}
            />
            <CircleChallengeGraph
              title={t('challenge.hero.title')}
              subtitle={t('challenge.stats.daysLeft.subtitle')}
              value={challenge?.isActive ? challenge?.daysLeft : 0}
              colour="#FCC954"
              width="quarter"
              icon={faCalendar}
              unit={
                challenge?.isActive || isChallengeUnavailable
                  ? t('challenge.stats.daysLeft.unit')
                  : t('challenge.stats.daysLeft.unit.ended')
              }
              isActive={challenge?.isActive || isChallengeUnavailable}
            />
            <LeaderBoard
              isActive={challenge?.isActive}
              isChallengeUnavailable={isChallengeUnavailable}
              handleToggle={handleToggleConsentSwitch}
              isOn={challenge?.userGaveConsent}
              leaders={challenge?.leaders || []}
              users={challenge?.users || []}
              challengeType={challenge?.type || ''}
              userPlace={challenge?.userPlace}
              buttonText={t('challenge.leaderboard.button.track')}
              handleButtonClick={() => toggleActivePage(2)}
            />
            {(challenge?.isActive || isChallengeUnavailable) && (
              <CircleChallengeGraph
                title={t('challenge.stats.userProgress.title')}
                subtitle={t('challenge.stats.userProgress.today')}
                value={getFormattedValue(challenge?.userProgressToday ?? 0, challenge?.type)}
                colour="#41C0D3"
                width="quarter"
                icon={getChallengeIcon(getChallengeType(sectionid))}
                unit={
                  !isChallengeUnavailable
                    ? getChallengeTypeUnit(challenge?.type, challenge?.userProgressToday)
                    : getChallengeTypeUnit(getChallengeType(sectionid), 0)
                }
              />
            )}
            <CircleChallengeGraph
              title={
                challenge?.isActive || isChallengeUnavailable
                  ? t('challenge.stats.userProgress.title')
                  : t('challenge.stats.userResults.title')
              }
              subtitle={
                challenge?.isActive || isChallengeUnavailable
                  ? t('challenge.stats.userProgress.overall')
                  : t('challenge.stats.total')
              }
              value={getFormattedValue(challenge?.userProgressOverall ?? 0, challenge?.type)}
              colour="#62BF92"
              width="quarter"
              icon={getChallengeIcon(getChallengeType(sectionid))}
              unit={
                !isChallengeUnavailable
                  ? getChallengeTypeUnit(challenge?.type, challenge?.userProgressOverall)
                  : getChallengeTypeUnit(getChallengeType(sectionid), 0)
              }
            />
            <CircleChallengeGraph
              title={
                challenge?.isActive || isChallengeUnavailable
                  ? t('challenge.stats.teamProgress.title')
                  : t('challenge.stats.teamResults.title')
              }
              subtitle={
                challenge?.isActive || isChallengeUnavailable
                  ? t('challenge.stats.teamProgress.completed')
                  : t('challenge.stats.total')
              }
              value={getFormattedValue(challenge?.teamProgressCompleted ?? 0, challenge?.type)}
              colour="#41C0D3"
              width="quarter"
              icon={getChallengeIcon(getChallengeType(sectionid))}
              unit={
                !isChallengeUnavailable
                  ? getChallengeTypeUnit(challenge?.type, challenge?.teamProgressCompleted)
                  : getChallengeTypeUnit(getChallengeType(sectionid), 0)
              }
            />
            {(challenge?.isActive || isChallengeUnavailable) && (
              <CircleChallengeGraph
                title={t('challenge.stats.teamProgress.title')}
                subtitle={t('challenge.stats.teamProgress.remaining')}
                value={getFormattedValue(challenge?.teamProgressRemaining ?? 0, challenge?.type)}
                colour="#62BF92"
                width="quarter"
                icon={getChallengeIcon(getChallengeType(sectionid))}
                unit={
                  !isChallengeUnavailable
                    ? getChallengeTypeUnit(challenge?.type, challenge?.teamProgressRemaining)
                    : getChallengeTypeUnit(getChallengeType(sectionid), 0)
                }
              />
            )}
            {(showConsentModal || showSubmitTrackerModal) && (
              <WhitePopUpCTA
                offline={false}
                popupContents={showConsentModal ? consentModalContents : submitTrackerModalContents}
                dialogTitle={
                  showConsentModal
                    ? t('modal.challenge.consent')
                    : t('modal.challenge.submitTracker')
                }
                onClose={handleCloseModal}
              />
            )}
            {showCompletedModal && (
              <FinishedChallenge
                title={
                  challenge?.teamProgressRemaining === 0
                    ? t('challenge.modal.finished.success.title')
                    : t('challenge.modal.finished.failure.title')
                }
                subtitle={
                  challenge?.teamProgressRemaining === 0
                    ? t('challenge.modal.finished.success.description')
                    : t('challenge.modal.finished.failure.description')
                }
                onClose={handleCloseCompletedModal}
                isSuccess={challenge?.teamProgressRemaining === 0}
              />
            )}
          </div>
        </div>
      )}
      {errorText && (
        <ChallengeErrorModal
          subtitle={errorText}
          onClose={() => setErrorText(null)}
          returnFocus={bringFocusToChallengeNavBtn}
        />
      )}
    </div>
  );
}

export default Challenge;
